import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileText, Filter } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api, { BACKEND_URL } from '../../lib/api';

const fmtRs = (v) => `Rs. ${Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const InventoryReport = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: {}, rows: [], categories: [] });
  const [category, setCategory] = useState('');
  const [stockStatus, setStockStatus] = useState('');

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (stockStatus) params.set('stock_status', stockStatus);
      const r = await api.get(`/reports/inventory?${params}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const exportPdf = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token });
    if (category) params.set('category', category);
    if (stockStatus) params.set('stock_status', stockStatus);
    window.open(`${BACKEND_URL}/api/reports/inventory/export-pdf?${params}`, '_blank');
  };

  const exportCsv = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token });
    if (category) params.set('category', category);
    if (stockStatus) params.set('stock_status', stockStatus);
    window.open(`${BACKEND_URL}/api/reports/inventory/export-csv?${params}`, '_blank');
  };

  const s = data.summary || {};
  const rowColor = (st) => {
    if (st === 'out_of_stock') return 'bg-[#FEF2F2]';
    if (st === 'low_stock') return 'bg-[#FFFBEB]';
    return '';
  };
  const statusBadge = (st) => {
    if (st === 'out_of_stock') return <span className="px-2 py-0.5 rounded-sm text-xs font-medium bg-[#FEE2E2] text-[#991B1B]">Out of Stock</span>;
    if (st === 'low_stock') return <span className="px-2 py-0.5 rounded-sm text-xs font-medium bg-[#FEF3C7] text-[#92400E]">Low Stock</span>;
    return <span className="px-2 py-0.5 rounded-sm text-xs font-medium bg-[#DBEAFE] text-[#1E40AF]">In Stock</span>;
  };
  const totalStockValue = (data.rows || []).reduce((a, r) => a + r.stock_value, 0);

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="inventory-report-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="inventory-report-title">Inventory Report</h1>
            <div className="flex gap-2">
              <button onClick={exportPdf} className="flex items-center gap-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white px-4 py-2 rounded-sm text-sm" data-testid="export-pdf-btn"><FileText className="w-4 h-4" />Export PDF</button>
              <button onClick={exportCsv} className="flex items-center gap-2 border border-[#e5e7eb] bg-white text-[#1a1a2e] px-4 py-2 rounded-sm text-sm hover:bg-[#F5F5F5] transition-colors" data-testid="export-csv-btn"><Download className="w-4 h-4" />Export CSV</button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-4 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="inventory-report-filters">
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)} className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm" data-testid="category-filter">
                <option value="">All Categories</option>
                {(data.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Stock Status</label>
              <select value={stockStatus} onChange={e => setStockStatus(e.target.value)} className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm" data-testid="stock-status-filter">
                <option value="">All</option>
                <option value="in_stock">In Stock</option>
                <option value="low_stock">Low Stock</option>
                <option value="out_of_stock">Out of Stock</option>
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={fetchReport} className="w-full flex items-center justify-center gap-2 bg-[#1a1a2e] text-white px-4 py-2 rounded-sm text-sm hover:bg-[#27272A] transition-colors" data-testid="apply-filters-btn"><Filter className="w-4 h-4" />Apply</button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="total-products-card">
              <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Total Products</p>
              <p className="text-xl font-semibold text-[#1a1a2e]">{s.total_products || 0}</p>
            </div>
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="total-stock-value-card">
              <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Total Stock Value</p>
              <p className="text-xl font-semibold text-[#1E40AF]">{fmtRs(s.total_stock_value)}</p>
            </div>
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="low-stock-count-card">
              <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Low Stock Items</p>
              <p className="text-xl font-semibold text-[#F59E0B]">{s.low_stock_count || 0}</p>
            </div>
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="out-of-stock-count-card">
              <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Out of Stock</p>
              <p className="text-xl font-semibold text-[#EF4444]">{s.out_of_stock_count || 0}</p>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-[#6b7280]">Loading...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left min-w-[600px]" data-testid="inventory-report-table">
                  <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                    <tr>
                      <th className="py-3 px-4">SKU</th>
                      <th className="py-3 px-4">Product Name</th>
                      <th className="py-3 px-4">Category</th>
                      <th className="py-3 px-4">Unit</th>
                      <th className="py-3 px-4 text-right">Current Stock</th>
                      <th className="py-3 px-4 text-right">Reorder Level</th>
                      <th className="py-3 px-4 text-right">Cost Price</th>
                      <th className="py-3 px-4 text-right">Stock Value</th>
                      <th className="py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.rows || []).map((row, i) => (
                      <tr key={i} className={`border-b border-[#e5e7eb] hover:bg-[#f9fafb] ${rowColor(row.stock_status)}`}>
                        <td className="py-3 px-4 font-medium text-[#1a1a2e]">{row.sku}</td>
                        <td className="py-3 px-4 text-[#1a1a2e]">{row.name}</td>
                        <td className="py-3 px-4 text-[#6b7280]">{row.category}</td>
                        <td className="py-3 px-4 text-[#6b7280]">{row.unit}</td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{row.current_stock}</td>
                        <td className="py-3 px-4 text-right text-[#6b7280]">{row.reorder_level}</td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{fmtRs(row.unit_cost)}</td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{fmtRs(row.stock_value)}</td>
                        <td className="py-3 px-4">{statusBadge(row.stock_status)}</td>
                      </tr>
                    ))}
                    {(data.rows || []).length > 0 && (
                      <tr className="bg-[#F5F5F5] font-bold border-t-2 border-[#e5e7eb]" data-testid="inventory-totals-row">
                        <td className="py-3 px-4 text-[#1a1a2e]" colSpan={7}>TOTAL</td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{fmtRs(totalStockValue)}</td>
                        <td className="py-3 px-4"></td>
                      </tr>
                    )}
                    {(data.rows || []).length === 0 && (
                      <tr><td colSpan={9} className="py-8 text-center text-[#9ca3af]">No products found for the selected filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default InventoryReport;
